import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { List, PersonCircle, BoxArrowRight, HouseDoor, Tools } from "react-bootstrap-icons";
import { useMsal } from "@azure/msal-react";
import Footer from "../components/Footer";
import useSessionChecker from "../util/useSessionChecker";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "../util/authConfig"; // adjust path if needed

const Container = styled.div`
  display: flex;
  height: 100vh;
  background-color: #f8f9fa;
  color: black;
`;

const Sidebar = styled.div<{ isOpen: boolean }>`
  width: ${(props) => (props.isOpen ? "250px" : "60px")};
  background: #FF372F;
  color: white;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease-in-out;
  overflow: hidden;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 1000;

  @media (max-width: 768px) {
    width: ${(props) => (props.isOpen ? "250px" : "0px")};
  }
`;

const SidebarItem = styled(Link)`
  display: flex;
  align-items: center;
  padding: 12px 15px;
  text-decoration: none;
  color: white;
  font-size: 16px;
  white-space: nowrap;
  transition: background 0.3s;
  border-radius: 5px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  svg {
    margin-right: 10px;
  }

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #000000;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  padding: 10px 20px;
  position: fixed;
  left: 60px;
  right: 0;
  top: 0;
  height: 60px;
  z-index: 999;
  transition: left 0.3s ease-in-out;

  @media (max-width: 768px) {
    left: 0;
  }
`;

const Content = styled.div<{ isOpen: boolean }>`
  flex: 1;
  padding: 100px 20px 20px 20px;
  overflow-y: auto;
  margin-left: ${(props) => (props.isOpen ? "250px" : "60px")};
  transition: margin-left 0.3s ease-in-out;

  // Use calc to dynamically adjust height based on header
  height: calc(100vh - 150px);

  @media (max-width: 768px) {
    margin-left: 0;
    padding: 106px 20px 20px 20px;
    height: calc(100vh - 156px); // Adjust height as per mobile header
  }
`;

const ToggleButton = styled.div`
  font-size: 24px;
  cursor: pointer;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  margin-right: 20px;
`;

const ProfileSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: bold;
  color: #FFFFFF;
  cursor: pointer;
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  display: ${(props) => (props.isOpen ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  @media (min-width: 769px) {
    display: none;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 5px;
  width: 80%;
`;

const LogoImage = styled.img`
  width: 80%;
  max-width: 150px;
  height: auto;
`;

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useSessionChecker();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const [userName, setUserName] = useState<string>("");
 const [userPhoto, setUserPhoto] = useState<string>("");

  const { instance, accounts } = useMsal(); // Get authentication instance and accounts

useEffect(() => {
  if (accounts.length > 0) {
    const fullName = accounts[0]?.name || "";
    if (fullName.includes(",")) {
      const [lastName, firstName] = fullName.split(",").map((part) => part.trim());
      setUserName(`${firstName} ${lastName}`);
    } else {
      setUserName(fullName);
    }

    getUserProfile();
  }
}, [accounts]);



const getUserProfile = async () => {
  try {
    const response = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });

    const accessToken = response.accessToken;

    // Fetch profile photo (returns blob)
    const photoResponse = await fetch("https://graph.microsoft.com/v1.0/me/photo/$value", {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (photoResponse.ok) {
      const photoBlob = await photoResponse.blob();
      const photoUrl = URL.createObjectURL(photoBlob);
      setUserPhoto(photoUrl);
    }

  } catch (error) {
    console.error("Error fetching user profile photo:", error);
  }
};

  // 🔴 Logout function
  const handleLogout = () => {
    instance.logoutPopup();
  };

  return (
    <>
      <Overlay isOpen={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      <Container>
        <Sidebar isOpen={sidebarOpen}>
          <LogoContainer>
            <LogoImage src="/wsplogowhite.png" alt="WSP Logo" />
          </LogoContainer>
          <SidebarItem to="/welcome" onClick={() => setSidebarOpen(false)}>
            <HouseDoor /> {sidebarOpen && "Home"}
          </SidebarItem>
          <SidebarItem to="/calculator-selection" onClick={() => setSidebarOpen(false)}>
            <Tools /> {sidebarOpen && "Tools"}
          </SidebarItem>
          <SidebarItem to="/login" onClick={handleLogout} style={{ background: "transparent", border: "none", cursor: "pointer" }}>
            <BoxArrowRight /> {sidebarOpen && "Logout"}
          </SidebarItem>
        </Sidebar>

        {/* Main Content */}
        <div style={{ flex: 1 }}>
          <Header>
            <ToggleButton onClick={() => setSidebarOpen(!sidebarOpen)}>
              <List color="white" />
            </ToggleButton>
              <ProfileSection>
                    {userPhoto ? (
                      <img
                        src={userPhoto}
                        alt="Profile"
                        style={{ width: "42px", height: "42px", borderRadius: "50%", marginRight: "8px" }}
                      />
                    ) : (
                      <PersonCircle size={32} style={{ marginRight: "8px" }} />
                    )}
                    <span>{userName || "User"}</span>
            </ProfileSection>
          </Header>
          <Content isOpen={sidebarOpen}>{children}</Content>
        </div>
        <Footer/>
      </Container>
    </>
  );
};

export default Layout;
